// The fields of an app's ASO.Market release plan: priority countries, the
// install keyword and the review keywords, plus per-country counts when the
// Settings defaults do not fit. Shared by the plan editor and the app wizard.
import { useState } from 'react';
import { CountryMultiSelect } from '@/components/CountryMultiSelect';
import type { AsomPlan } from '@/api/asoMarket';
import { FieldLabel, INPUT } from './ui';

export const EMPTY_PLAN: AsomPlan = { countries: [], install_keyword: null, review_keywords: [], installs: null, reviews: null };

/** One per line or comma separated, like the server reads them. */
export function parseKeywords(text: string): string[] {
  const out: string[] = [];
  for (const p of text.split(/[\n,;]/)) {
    const k = p.trim().replace(/\s+/g, ' ');
    if (k && !out.some((x) => x.toLowerCase() === k.toLowerCase())) out.push(k);
  }
  return out;
}

export function PlanFields({ value, onChange, sellable, defaults, counts = true }: {
  value: AsomPlan;
  onChange: (plan: AsomPlan) => void;
  /** Countries ASO.Market takes orders in; all countries when unknown. */
  sellable?: string[];
  /** Settings' per-country counts, shown as placeholders. */
  defaults?: { installs: number; reviews: number };
  counts?: boolean;
}) {
  // The textarea keeps what was typed (a trailing comma, a blank line).
  const [reviewText, setReviewText] = useState(value.review_keywords.join('\n'));
  const set = (patch: Partial<AsomPlan>) => onChange({ ...value, ...patch });
  const count = (raw: string): number | null => (raw === '' ? null : Math.max(0, Math.floor(Number(raw) || 0)));

  return (
    <div className="space-y-4">
      <div>
        <FieldLabel aside="each gets its own local campaigns">Priority countries</FieldLabel>
        <CountryMultiSelect
          value={value.countries}
          onChange={(countries) => set({ countries })}
          restrictTo={sellable?.length ? sellable : undefined}
          placeholder="Search countries…"
        />
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div>
          <FieldLabel aside="what performers search">Install keyword</FieldLabel>
          <input
            className={INPUT} value={value.install_keyword ?? ''} maxLength={100} placeholder="Empty = search by app name"
            onChange={(e) => set({ install_keyword: e.target.value || null })}
          />
        </div>
        <div>
          <FieldLabel aside="one per line">Review keywords</FieldLabel>
          <textarea
            className={`${INPUT} min-h-[38px]`} rows={2} value={reviewText} placeholder="Each review carries one or two"
            onChange={(e) => { setReviewText(e.target.value); set({ review_keywords: parseKeywords(e.target.value) }); }}
          />
        </div>
      </div>
      {counts && (
        <div className="grid grid-cols-2 gap-4 max-w-md">
          <div>
            <FieldLabel aside="per country">Installs</FieldLabel>
            <input
              className={INPUT} type="number" min={0} value={value.installs ?? ''}
              placeholder={defaults ? `${defaults.installs} (Settings)` : 'Settings'}
              onChange={(e) => set({ installs: count(e.target.value) })}
            />
          </div>
          <div>
            <FieldLabel aside="per country">Reviews</FieldLabel>
            <input
              className={INPUT} type="number" min={0} value={value.reviews ?? ''}
              placeholder={defaults ? `${defaults.reviews} (Settings)` : 'Settings'}
              onChange={(e) => set({ reviews: count(e.target.value) })}
            />
          </div>
        </div>
      )}
    </div>
  );
}
